import React from 'react';
import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { MessageCirclePlus } from 'lucide-react';
import { BaseTool, type IToolManifest, type IToolComponentProps, type IToolContext } from '../../core/tools/interfaces/ITool';
import type { ICommand } from '../../core/commands/ICommand';
import { Button } from '../../components/ui/button';
import type { CommentShapeLocation } from '../../domains/comments/models/Comment';
import { computeCommentRelativeLocOnShape } from '../../lib/commentShapePlacement';
import { BeginCommentPlacementCommand } from '../../core/commands/BeginCommentPlacementCommand';

const COMMENT_CURSOR = "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2224%22 height=%2224%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%232563EB%22 stroke-width=%222.2%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpath d=%22M21 11.5a8.38 8.38 0 0 1-9 8.5 8.18 8.18 0 0 1-3.95-1l-4.05 1 .87-3.8A8.5 8.5 0 1 1 21 11.5Z%22/%3E%3Cpath d=%22M12 8v7%22/%3E%3Cpath d=%22M8.5 11.5h7%22/%3E%3C/svg%3E') 12 12, pointer";

const CommentsToolButton: React.FC<IToolComponentProps> = ({ onExecute }) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      data-testid="tid.tools.comments.trigger"
      className="w-10 h-10 rounded-lg"
      onClick={() => onExecute()}
    >
      <MessageCirclePlus className="h-5 w-5" />
    </Button>
  );
};

type PlacementCoords = {
  diagram: {
    x: number;
    y: number;
  };
  local: {
    x: number;
    y: number;
  };
  screen: {
    x: number;
    y: number;
  };
  shapeId: string | null;
  locOnShape: CommentShapeLocation | null;
};

export class CommentsTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'comments',
    name: 'Comments',
    icon: 'comments',
    category: 'create',
    description: 'Add comment pins to the canvas',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram', 'database', 'flowchart', 'network', 'uml', 'generic', 'org'],
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: false,
      statusBar: false,
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false,
    },
    shortcuts: [
      {
        key: 'M',
        modifiers: ['shift'],
        description: 'Activate comments tool',
      },
    ],
  };

  private placing = false;
  private promptOpen = false;
  private unsubscribeCompleted: (() => void) | null = null;
  private unsubscribeCancelled: (() => void) | null = null;

  onActivate(context: IToolContext): void {
    super.onActivate(context);
    if (context.stage) {
      context.stage.off('click.comments-tool');
      context.stage.on('click.comments-tool', this.handleStageClick);
    }
    if (context.eventBus) {
      this.unsubscribeCompleted = context.eventBus.on('comment:placement:completed', this.resetPlacementState);
      this.unsubscribeCancelled = context.eventBus.on('comment:placement:cancelled', this.resetPlacementState);
    }
  }

  onDeactivate(): void {
    if (this.context?.stage) {
      this.context.stage.off('click.comments-tool');
      this.restoreCursor();
    }
    if (this.context?.eventBus) {
      if (this.unsubscribeCompleted) {
        this.unsubscribeCompleted();
        this.unsubscribeCompleted = null;
      }
      if (this.unsubscribeCancelled) {
        this.unsubscribeCancelled();
        this.unsubscribeCancelled = null;
      }
    }
    this.placing = false;
    this.promptOpen = false;
    super.onDeactivate();
  }

  execute(): ICommand | null {
    if (!this.context) {
      return null;
    }

    return new BeginCommentPlacementCommand({
      beginPlacement: () => {
        this.placing = true;
        this.promptOpen = false;
        this.applyCommentCursor();
      },
      cancelPlacement: this.resetPlacementState,
      eventBus: this.context.eventBus,
    });
  }

  getToolbarComponent() {
    return CommentsToolButton;
  }

  private handleStageClick = (evt: KonvaEventObject<MouseEvent>) => {
    if (!this.placing || this.promptOpen || !this.context?.stage) {
      return;
    }
    if (evt.evt.button !== 0) {
      return;
    }

    const stage = this.context.stage;
    const pointer = stage.getPointerPosition();
    if (!pointer) {
      return;
    }

    const scale = stage.scaleX();
    const position = stage.position();
    const diagramX = (pointer.x - position.x) / scale;
    const diagramY = (pointer.y - position.y) / scale;

    const containerRect = stage.container().getBoundingClientRect();
    const { shapeId, locOnShape } = this.resolveShapePlacement({ x: diagramX, y: diagramY }, evt.target || null);

    const coords: PlacementCoords = {
      diagram: { x: diagramX, y: diagramY },
      local: { x: pointer.x, y: pointer.y },
      screen: {
        x: containerRect.left + pointer.x,
        y: containerRect.top + pointer.y,
      },
      shapeId,
      locOnShape,
    };

    this.context.eventBus?.emit('comment:placement:open', coords);
    this.promptOpen = true;
    this.restoreCursor();
  };

  private resolveShapePlacement(
    diagramPoint: { x: number; y: number },
    target: Konva.Node | null,
  ): { shapeId: string | null; locOnShape: CommentShapeLocation | null } {
    if (!target) {
      return { shapeId: null, locOnShape: null };
    }

    const shapeNode = target.findAncestor((node: Konva.Node) => node.getAttr('name') === 'shape', true) as Konva.Node | null
      || (target.getAttr('name') === 'shape' ? target : null);

    if (!shapeNode) {
      return { shapeId: null, locOnShape: null };
    }

    const shapeId = shapeNode.id();
    if (!shapeId) {
      return { shapeId: null, locOnShape: null };
    }

    const locOnShape = this.computeRelativeLocation(shapeId, diagramPoint);
    if (!locOnShape) {
      return { shapeId, locOnShape: null };
    }

    return { shapeId, locOnShape };
  }

  private computeRelativeLocation(shapeId: string, diagramPoint: { x: number; y: number }): CommentShapeLocation | null {
    if (!this.context) {
      return null;
    }
    return computeCommentRelativeLocOnShape(shapeId, diagramPoint, this.context.shapes);
  }

  private resetPlacementState = () => {
    // Reset internal state
    this.placing = false;
    this.promptOpen = false;
    // Only restore cursor if tool is still active (to avoid conflicts with onDeactivate)
    // If tool is being deactivated, onDeactivate will handle cursor restoration
    if (this.context) {
      this.restoreCursor();
    }
  };

  private applyCommentCursor() {
    try {
      const container = this.context?.stage?.container();
      if (container) {
        container.setAttribute('data-comment-cursor', '1');
        container.style.setProperty('cursor', COMMENT_CURSOR);
      }
    } catch (error) {
      console.warn('Failed to apply comment cursor', error);
    }
  }

  private restoreCursor() {
    try {
      const container = this.context?.stage?.container();
      if (container) {
        container.removeAttribute('data-comment-cursor');
        container.style.removeProperty('cursor');
      }
    } catch (error) {
      console.warn('Failed to restore cursor', error);
    }
  }
}
